<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { fade, slide } from "svelte/transition";
  import { api, getToken, handleApiError } from "../lib/api";
  import QRCode from "qrcode";
  import { addToast } from "../lib/toast";
  import ConfirmModal from "../components/ui/ConfirmModal.svelte";

  // Helper function to get session type name
  function getSessionTypeName(type?: string): string {
    switch (type) {
      case "telegram_mtproto":
        return "Telegram MTProto";
      case "telegram_bot":
        return "Telegram Bot";
      case "email":
        return "Email";
      case "whatsapp":
      default:
        return "WhatsApp";
    }
  }

  // Helper function to check if session supports QR
  function supportsQR(type?: string): boolean {
    return type === "whatsapp" || !type;
  }

  function formatRestrictedUntil(until?: string | null): string {
    if (!until) return "";
    const date = new Date(until);
    if (Number.isNaN(date.getTime())) return until;
    return date.toLocaleString("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  type Session = {
    id: string;
    label: string;
    number: string;
    type?: string;
    connected?: boolean;
    restricted?: boolean;
    restricted_until?: string | null;
    telegram_config?: string | object; // JSON string o objeto parseado
  };

  let sessions: Session[] = [];
  let loading = false;
  let creating = false;

  // Create form
  let newLabel = "";
  let newNumber = "";
  let newType = "whatsapp"; // Default: whatsapp
  let showCreateModal = false;

  // Configurations (conditional based on type)
  let telegramConfig = {
    api_id: "",
    api_hash: "",
    phone_number: "",
    password: "",
    bot_token: "",
  };
  let emailConfig = {
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    from_email: "",
    from_name: "",
    tls: true,
  };

  // Connection / QR
  let selectedSession: Session | null = null;
  let ws: WebSocket | null = null;
  let wsConnected = false;
  let wspState: "ok" | "problem" | "down" = "down";
  let wspHint = "";
  let wspPollInterval: ReturnType<typeof setInterval> | null = null;
  let qrImage = "";
  let statusText = "";
  let logs: string[] = [];

  // Confirm modal
  let showConfirm = false;
  let confirmAction: "disconnect" | "logout" | "delete" | null = null;
  let confirmSessionId = "";

  // API Info Modal State
  let showAPIInfoModal = false;
  let apiInfoSessionId = "";
  let apiInfoLoading = false;
  let apiInfo: { appkey: string; authkey: string } | null = null;

  // API QR State
  let apiQRImage = "";
  let apiQRTempUrl = "";
  let generatingTempUrl = false;
  let qrToken = "";
  let qrStatus: {
    active: boolean;
    expired: boolean;
    requests_used: number;
    requests_remaining: number;
    max_requests: number;
    seconds_remaining: number;
    expires_in_minutes: number;
  } | null = null;
  let checkingStatus = false;
  let statusCheckInterval: ReturnType<typeof setInterval> | null = null;

  // Telegram Code Modal State
  let showTelegramCodeModal = false;
  let telegramCode = "";
  let telegramCodeLoading = false;
  let telegramCodeSessionId = "";
  let resendCodeCountdown = 0;
  let resendCodeInterval: ReturnType<typeof setInterval> | null = null;

  // Telegram Phone Modal State
  let showTelegramPhoneModal = false;
  let telegramPhone = "";
  let telegramPhoneLoading = false;
  let telegramPhoneSessionId = "";

  // Telegram Password Modal State
  let showTelegramPasswordModal = false;
  let telegramPassword = "";
  let telegramPasswordLoading = false;
  let telegramPasswordSessionId = "";

  async function loadSessions() {
    loading = true;
    try {
      const response = await api.get("/api/v1/sessions");
      // Asegurar que siempre sea un array
      if (Array.isArray(response)) {
        sessions = response;
      } else if (response && Array.isArray(response.sessions)) {
        sessions = response.sessions;
      } else if (response && Array.isArray(response.data)) {
        sessions = response.data;
      } else {
        sessions = [];
      }
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(errorMsg, "error");
      sessions = []; // Asegurar que sessions sea un array vacío en caso de error
    } finally {
      loading = false;
    }
  }

  async function createSession() {
    if (!newLabel) return;
    creating = true;
    try {
      const payload: any = {
        label: newLabel,
        number: newNumber,
        type: newType,
      };

      // Add configuration based on type
      if (newType === "telegram_mtproto" || newType === "telegram_bot") {
        if (newType === "telegram_mtproto") {
          payload.telegram_config = {
            api_id: parseInt(telegramConfig.api_id) || 0,
            api_hash: telegramConfig.api_hash,
            phone_number: telegramConfig.phone_number || "", // Solo usar el campo específico, no newNumber
            password: telegramConfig.password || "",
          };
          // Para Telegram MTProto, el campo number puede quedar vacío o usar el phone_number como referencia
          if (!payload.number && telegramConfig.phone_number) {
            payload.number = telegramConfig.phone_number;
          }
        } else {
          payload.telegram_config = {
            bot_token: telegramConfig.bot_token,
          };
        }
      } else if (newType === "email") {
        payload.email_config = {
          smtp_host: emailConfig.smtp_host,
          smtp_port: parseInt(emailConfig.smtp_port.toString()) || 587,
          smtp_user: emailConfig.smtp_user,
          smtp_password: emailConfig.smtp_password,
          from_email: emailConfig.from_email || emailConfig.smtp_user,
          from_name: emailConfig.from_name || "",
          tls: emailConfig.tls,
        };
      }

      const response = await api.post("/api/v1/sessions", payload);
      const s = response.data || response; // Soportar ambos formatos
      sessions = [s, ...sessions];
      newLabel = "";
      newNumber = "";
      newType = "whatsapp";
      telegramConfig = {
        api_id: "",
        api_hash: "",
        phone_number: "",
        password: "",
        bot_token: "",
      };
      emailConfig = {
        smtp_host: "",
        smtp_port: 587,
        smtp_user: "",
        smtp_password: "",
        from_email: "",
        from_name: "",
        tls: true,
      };
      showCreateModal = false;
      addToast("Sesión creada correctamente", "success");
      
      // Si es Telegram MTProto y tiene phone_number, seleccionar y conectar automáticamente
      if (s.type === "telegram_mtproto" && s.telegram_config) {
        try {
          const config = typeof s.telegram_config === "string" 
            ? JSON.parse(s.telegram_config) 
            : s.telegram_config;
          if (config?.phone_number && config.phone_number.trim() !== "") {
            // Seleccionar la sesión y conectar automáticamente
            await selectSession(s);
          }
        } catch (e) {
          console.error("Error parseando telegram_config:", e);
        }
      }
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(errorMsg, "error");
    } finally {
      creating = false;
    }
  }

  function wspLabel(state: typeof wspState): string {
    if (state === "ok") return "WSP ACTIVO";
    if (state === "problem") return "WSP PROBLEMA";
    return "WSP INACTIVO";
  }

  function applyWspFromStatus(statusResponse: any) {
    const state = statusResponse?.wsp_state;
    if (state === "ok" || state === "problem" || state === "down") {
      wspState = state;
    } else {
      wspState = statusResponse?.live || statusResponse?.connected ? "ok" : "down";
    }
    const parts: string[] = [];
    if (statusResponse?.live === false && statusResponse?.paired) {
      parts.push("WhatsApp vinculado, socket caído");
    }
    if (statusResponse?.last_send_ok === false && statusResponse?.last_send_error) {
      parts.push(statusResponse.last_send_error);
    } else if (statusResponse?.last_send_ok === true) {
      parts.push("Último envío aceptado por WhatsApp");
    }
    if (statusResponse?.send_failures > 0) {
      parts.push(`Fallos internos: ${statusResponse.send_failures}`);
    }
    wspHint = parts.join(" · ");
  }

  function stopWspPolling() {
    if (wspPollInterval) {
      clearInterval(wspPollInterval);
      wspPollInterval = null;
    }
  }

  function startWspPolling(sessionId: string) {
    stopWspPolling();
    wspPollInterval = setInterval(async () => {
      if (!selectedSession || selectedSession.id !== sessionId) return;
      try {
        const statusResponse = await api.get(
          `/api/v1/sessions/${encodeURIComponent(sessionId)}/status`
        );
        applyWspFromStatus(statusResponse);
      } catch {
        wspState = "down";
        wspHint = "No se pudo leer estado de WhatsApp";
      }
    }, 5000);
  }

  async function selectSession(s: Session) {
    if (selectedSession?.id === s.id) return;
    disconnectWS();
    stopWspPolling();
    selectedSession = s;
    qrImage = "";
    logs = [];
    wspState = "down";
    wspHint = "";

    // Verificar el estado real de la sesión antes de asumir que está conectada
    let isActuallyConnected = false;
    if (s.connected) {
      try {
        const statusResponse = await api.get(
          `/api/v1/sessions/${encodeURIComponent(s.id)}/status`
        );
        isActuallyConnected = statusResponse.connected === true;
        applyWspFromStatus(statusResponse);
      } catch (e) {
        // Si falla la verificación, asumir que no está conectada
        isActuallyConnected = false;
        wspState = "down";
      }
    }

    startWspPolling(s.id);

    // Si la sesión realmente está conectada, solo mostrar información
    if (isActuallyConnected) {
      statusText = "✅ Sesión Conectada";
      // Actualizar el estado en el array local
      sessions = sessions.map((sess) =>
        sess.id === s.id ? { ...sess, connected: true } : sess
      );
      selectedSession = { ...selectedSession, connected: true };
      return;
    }

    // Si no está conectada (o el estado era incorrecto), actualizar y conectar
    sessions = sessions.map((sess) =>
      sess.id === s.id ? { ...sess, connected: false } : sess
    );
    selectedSession = { ...selectedSession, connected: false };

    statusText =
      s.type === "telegram_mtproto"
        ? "Conectando a Telegram..."
        : "Conectando...";
    
    // Para Telegram MTProto, verificar si tiene número telefónico ANTES de conectar
    if (s.type === "telegram_mtproto") {
      let hasPhoneNumber = false;
      let telegramConfig = null;
      if (s.telegram_config) {
        try {
          telegramConfig = typeof s.telegram_config === "string" 
            ? JSON.parse(s.telegram_config) 
            : s.telegram_config;
          hasPhoneNumber = telegramConfig?.phone_number && telegramConfig.phone_number.trim() !== "";
        } catch (e) {
          // Si no se puede parsear, asumir que no tiene
          console.error("Error parseando telegram_config:", e);
          hasPhoneNumber = false;
        }
      }
      
      if (!hasPhoneNumber) {
        // Mostrar modal para solicitar número telefónico
        telegramPhoneSessionId = s.id;
        showTelegramPhoneModal = true;
        statusText = "Número telefónico requerido";
        
        // Log detallado de la configuración actual
        const configInfo = telegramConfig 
          ? `API ID: ${telegramConfig.api_id || "NO DEFINIDO"}, API Hash: ${telegramConfig.api_hash ? telegramConfig.api_hash.substring(0, 10) + "..." : "NO DEFINIDO"}, Phone Number: ${telegramConfig.phone_number || "VACÍO"}`
          : "Configuración de Telegram no encontrada o no parseable";
        
        logs = [
          "📱 Se requiere un número telefónico para conectar Telegram",
          `📋 Configuración actual: ${configInfo}`,
          ...logs,
        ];
        
        console.log("⚠️ [SESSIONS] Número telefónico faltante para sesión:", s.id);
        console.log("📋 [SESSIONS] Configuración de Telegram:", telegramConfig);
        console.log("📋 [SESSIONS] API ID:", telegramConfig?.api_id);
        console.log("📋 [SESSIONS] API Hash:", telegramConfig?.api_hash ? telegramConfig.api_hash.substring(0, 20) + "..." : "NO DEFINIDO");
        console.log("📋 [SESSIONS] Phone Number:", telegramConfig?.phone_number || "VACÍO");
        
        return; // No continuar con la conexión hasta tener el número
      } else {
        // Log cuando SÍ tiene número telefónico
        console.log("✅ [SESSIONS] Sesión tiene número telefónico, procediendo con conexión");
        console.log("📋 [SESSIONS] Configuración de Telegram:", {
          api_id: telegramConfig?.api_id,
          api_hash: telegramConfig?.api_hash ? telegramConfig.api_hash.substring(0, 20) + "..." : "NO DEFINIDO",
          phone_number: telegramConfig?.phone_number,
          has_password: !!telegramConfig?.password
        });
        logs = [
          `📋 Usando configuración: API ID: ${telegramConfig?.api_id || "N/A"}, Teléfono: ${telegramConfig?.phone_number || "N/A"}`,
          ...logs,
        ];
      }
    }
    
    try {
      // Para Telegram MTProto, conectar WebSocket ANTES de llamar a /connect
      // para asegurar que esté suscrito cuando llegue el evento code_required
      if (s.type === "telegram_mtproto") {
        console.log("📡 [SESSIONS] Conectando WebSocket ANTES de /connect para Telegram MTProto");
        connectWS(s.id);
        // Esperar un momento para que el WebSocket se suscriba completamente
        await new Promise(resolve => setTimeout(resolve, 1500));
        console.log("✅ [SESSIONS] WebSocket conectado, ahora llamando a /connect");
      }
      
      await api.post(`/api/v1/sessions/${encodeURIComponent(s.id)}/connect`);
      
      // Solo conectar WebSocket para WhatsApp (que usa QR) si no es Telegram MTProto
      if (s.type === "whatsapp" || !s.type) {
        connectWS(s.id);
      } else if (s.type !== "telegram_mtproto") {
        // Para otros tipos (telegram_bot, email), no necesitan WebSocket
        statusText = "Conectado";
      }
    } catch (e: any) {
      statusText = "Error: " + handleApiError(e);
    }
  }

  async function disconnectSession() {
    if (!confirmSessionId) return;
    try {
      await api.post(
        `/api/v1/sessions/${encodeURIComponent(confirmSessionId)}/disconnect`
      );
      addToast("Sesión desconectada correctamente", "success");
      await loadSessions();
      if (selectedSession?.id === confirmSessionId) {
        selectedSession = null;
        disconnectWS();
      }
    } catch (e: any) {
      addToast(handleApiError(e), "error");
    } finally {
      showConfirm = false;
      confirmAction = null;
      confirmSessionId = "";
    }
  }

  async function logoutSession() {
    if (!confirmSessionId) return;
    try {
      await api.post(
        `/api/v1/sessions/${encodeURIComponent(confirmSessionId)}/logout`
      );
      addToast(
        "Sesión desvinculada. Deberás escanear el QR nuevamente para reconectar.",
        "success"
      );
      await loadSessions();
      if (selectedSession?.id === confirmSessionId) {
        selectedSession = null;
        disconnectWS();
      }
    } catch (e: any) {
      addToast(handleApiError(e), "error");
    } finally {
      showConfirm = false;
      confirmAction = null;
      confirmSessionId = "";
    }
  }

  function openConfirm(action: "disconnect" | "logout", sessionId: string) {
    confirmAction = action;
    confirmSessionId = sessionId;
    showConfirm = true;
  }

  function getConfirmTitle(): string {
    switch (confirmAction) {
      case "disconnect":
        return "Desconectar Sesión";
      case "logout":
        return "Desvincular Sesión";
      default:
        return "Confirmar Acción";
    }
  }

  function getConfirmMessage(): string {
    switch (confirmAction) {
      case "disconnect":
        return "¿Estás seguro de que deseas desconectar esta sesión? La sesión se mantendrá pero se cerrará la conexión.";
      case "logout":
        return "¿Estás seguro de que deseas desvincular esta sesión? Deberás escanear el QR nuevamente para reconectar.";
      default:
        return "¿Estás seguro?";
    }
  }

  async function handleConfirm() {
    switch (confirmAction) {
      case "disconnect":
        await disconnectSession();
        break;
      case "logout":
        await logoutSession();
        break;
    }
  }

  let qrTimeout: ReturnType<typeof setTimeout> | null = null;
  let qrRetryCount = 0;
  const MAX_QR_RETRIES = 3;

  function connectWS(sessionId: string, isRetry = false) {
    // Limpiar timeout anterior si existe
    if (qrTimeout) {
      clearTimeout(qrTimeout);
      qrTimeout = null;
    }

    // Cerrar conexión anterior si existe
    if (ws) {
      ws.close();
      ws = null;
    }

    const proto = location.protocol === "https:" ? "wss" : "ws";
    const t = getToken();
    if (!t) {
      statusText = "Error: No hay token de autenticación";
      logs = [
        "Error: Token no encontrado. Por favor, inicia sesión nuevamente.",
        ...logs,
      ];
      return;
    }

    // En desarrollo, usar localhost:8060. En producción, usar el mismo dominio (location.host)
    const isDev =
      (import.meta as any).env?.DEV || location.hostname === "localhost";
    const host = isDev ? `${location.hostname}:8060` : location.host;
    const url = `${proto}://${host}/api/v1/ws?sessionId=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(t)}`;

    let qrReceived = false;

    try {
      ws = new WebSocket(url);

      ws.onopen = () => {
        wsConnected = true;
        logs = ["✅ Conectado al servidor", ...logs];
        console.log("✅ [WS] WebSocket conectado para sesión:", sessionId);

        // Solo esperar QR para WhatsApp, no para Telegram MTProto
        const isWhatsApp =
          selectedSession?.type === "whatsapp" || !selectedSession?.type;
        if (isWhatsApp) {
          statusText = "Conectado, esperando QR...";

          // Iniciar timeout de 5 segundos para recibir QR (solo para WhatsApp)
          qrTimeout = setTimeout(() => {
            if (!qrReceived && qrRetryCount < MAX_QR_RETRIES) {
              qrRetryCount++;
              logs = [
                `⏱️ QR no recibido en 5 segundos, reintentando... (${qrRetryCount}/${MAX_QR_RETRIES})`,
                ...logs,
              ];
              statusText = "Reintentando conexión...";
              // Cerrar WebSocket actual
              if (ws) {
                ws.close();
                ws = null;
              }
              // Reintentar después de un breve delay
              setTimeout(() => {
                connectWS(sessionId, true);
              }, 1000);
            } else if (!qrReceived && qrRetryCount >= MAX_QR_RETRIES) {
              logs = [
                "❌ No se pudo obtener QR después de varios intentos",
                ...logs,
              ];
              statusText =
                "Error: No se pudo generar el QR. Intenta nuevamente.";
              qrRetryCount = 0;
            }
          }, 5000); // 5 segundos
        } else if (selectedSession?.type === "telegram_mtproto") {
          statusText = "Conectado, esperando código de verificación...";
          logs = [
            "📱 Esperando código de verificación de Telegram...",
            "📱 El código llegará a tu aplicación móvil de Telegram (en tu celular)",
            "📱 Revisa tu celular cuando Telegram envíe el código",
            "📱 Cuando recibas el código, se abrirá un modal para ingresarlo",
            ...logs,
          ];
          console.log("✅ [WS] WebSocket conectado para Telegram MTProto, esperando evento code_required");
        } else {
          statusText = "Conectado";
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        logs = ["❌ Error de conexión WebSocket", ...logs];
        statusText = "Error de conexión";
        if (qrTimeout) {
          clearTimeout(qrTimeout);
          qrTimeout = null;
        }
      };

      ws.onclose = (event) => {
        wsConnected = false;
        if (qrTimeout) {
          clearTimeout(qrTimeout);
          qrTimeout = null;
        }
        if (event.code !== 1000) {
          logs = [`⚠️ Desconectado (código: ${event.code})`, ...logs];
          if (event.code === 1006) {
            statusText = "Conexión cerrada inesperadamente. Verifica tu token.";
          }
        } else {
          logs = ["Desconectado", ...logs];
        }
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "qr") {
            // QR recibido (solo para WhatsApp), cancelar timeout y resetear contador
            const isWhatsApp =
              selectedSession?.type === "whatsapp" || !selectedSession?.type;
            if (!isWhatsApp) {
              // Ignorar QR si no es WhatsApp
              logs = [
                "⚠️ QR recibido pero no aplica para este tipo de sesión",
                ...logs,
              ];
              return;
            }

            qrReceived = true;
            if (qrTimeout) {
              clearTimeout(qrTimeout);
              qrTimeout = null;
            }
            qrRetryCount = 0;

            QRCode.toDataURL(String(msg.data), {
              width: 300,
              margin: 2,
              color: { dark: "#000000", light: "#ffffff" },
            })
              .then((url) => (qrImage = url))
              .catch((err) => {
                console.error("Error generando QR:", err);
                logs = ["Error generando imagen QR", ...logs];
              });
            statusText = "Escanea el código QR";
            logs = ["📱 Código QR recibido", ...logs];
          } else if (msg.type === "status") {
            const statusData = msg.data || {};
            
            // Debug: log todos los eventos de status
            console.log("📡 [WS] Evento de status recibido:", statusData);

            // Manejar evento code_required para Telegram MTProto
            if (statusData.event === "code_required") {
              console.log("📱 [WS] Evento code_required recibido, abriendo modal...");
              console.log("📱 [WS] Datos completos del evento:", statusData);
              console.log("📱 [WS] Sesión seleccionada:", selectedSession?.id);
              
              // Cancelar timeout de QR si existe (no aplica para Telegram)
              if (qrTimeout) {
                clearTimeout(qrTimeout);
                qrTimeout = null;
              }
              qrRetryCount = 0;

              if (selectedSession && selectedSession.id) {
                telegramCodeSessionId = selectedSession.id;
                showTelegramCodeModal = true;
                
                // Inicializar contador de 15 segundos cuando se abre el modal
                resendCodeCountdown = 15;
                if (resendCodeInterval) {
                  clearInterval(resendCodeInterval);
                }
                resendCodeInterval = setInterval(() => {
                  resendCodeCountdown--;
                  if (resendCodeCountdown <= 0) {
                    if (resendCodeInterval) {
                      clearInterval(resendCodeInterval);
                      resendCodeInterval = null;
                    }
                  }
                }, 1000);
                
                statusText = "📱 Revisa tu aplicación móvil de Telegram";
                logs = [
                  "📱 Telegram ha enviado un código de verificación",
                  "📱 El código llegó a tu aplicación móvil de Telegram (en tu celular)",
                  "📱 Revisa tu celular y busca el código en la app de Telegram",
                  "📱 Ingresa el código en el modal que se abrió",
                  ...logs,
                ];
                console.log("✅ [WS] Modal de código abierto para sesión:", selectedSession.id);
              } else {
                console.error("❌ [WS] No hay sesión seleccionada, no se puede abrir modal");
                logs = [
                  "⚠️ Código recibido pero no hay sesión seleccionada",
                  ...logs,
                ];
              }
            } else if (statusData.event === "password_required") {
              console.log("🔐 [WS] Evento password_required recibido, abriendo modal...");
              console.log("🔐 [WS] Datos completos del evento:", statusData);
              console.log("🔐 [WS] Sesión seleccionada:", selectedSession?.id);
              
              if (selectedSession && selectedSession.id) {
                telegramPasswordSessionId = selectedSession.id;
                showTelegramPasswordModal = true;
                
                statusText = "🔐 Contraseña 2FA requerida";
                logs = [
                  "🔐 Telegram requiere tu contraseña de verificación en dos pasos (2FA)",
                  "🔐 Ingresa la contraseña que configuraste en Telegram",
                  "🔐 Si no recuerdas tu contraseña, puedes restablecerla desde la app de Telegram",
                  ...logs,
                ];
                console.log("✅ [WS] Modal de contraseña abierto para sesión:", selectedSession.id);
              } else {
                console.error("❌ [WS] No hay sesión seleccionada, no se puede abrir modal");
                logs = [
                  "⚠️ Contraseña requerida pero no hay sesión seleccionada",
                  ...logs,
                ];
              }
            } else if (statusData.connected) {
              // Conexión exitosa, cancelar timeout (solo para WhatsApp)
              const isWhatsApp =
                selectedSession?.type === "whatsapp" || !selectedSession?.type;
              if (isWhatsApp) {
                qrReceived = true;
                if (qrTimeout) {
                  clearTimeout(qrTimeout);
                  qrTimeout = null;
                }
                qrRetryCount = 0;
              }

              statusText = "✅ Vinculado y Conectado";
              qrImage = "";
              wspState = "ok";
              wspHint = "WhatsApp conectado";
              const sessionTypeName = getSessionTypeName(selectedSession?.type);
              logs = [`✅ ${sessionTypeName} conectado`, ...logs];

              // Cerrar modales de autenticación si estaban abiertos
              if (showTelegramCodeModal) {
                showTelegramCodeModal = false;
                telegramCode = "";
                telegramCodeSessionId = "";
              }
              if (showTelegramPasswordModal) {
                showTelegramPasswordModal = false;
                telegramPassword = "";
                telegramPasswordSessionId = "";
              }

              // Actualizar el estado de la sesión en el array
              if (selectedSession && selectedSession.id) {
                const sessionId = selectedSession.id;
                // Actualizar en el array de sesiones
                sessions = sessions.map((s) =>
                  s.id === sessionId ? { ...s, connected: true } : s
                );
                // Actualizar la sesión seleccionada
                selectedSession = { ...selectedSession, connected: true };

                // Recargar la lista completa para asegurar sincronización
                loadSessions();
              }
            } else {
              statusText = "Esperando conexión...";
            }
          } else if (msg.type === "error") {
            const errorMsg = String(msg.data || "Error desconocido");
            statusText = `Error: ${errorMsg}`;
            logs = [`❌ Error: ${errorMsg}`, ...logs];
            if (qrTimeout) {
              clearTimeout(qrTimeout);
              qrTimeout = null;
            }
          } else if (msg.type === "heartbeat") {
            // Silenciar heartbeats en logs
          }
        } catch (e) {
          console.error("Error parseando mensaje WebSocket:", e, ev.data);
          logs = [`Error parseando mensaje: ${e}`, ...logs];
        }
      };
    } catch (e: any) {
      statusText = `Error al conectar: ${e.message}`;
      logs = [`❌ Error al crear WebSocket: ${e.message}`, ...logs];
      if (qrTimeout) {
        clearTimeout(qrTimeout);
        qrTimeout = null;
      }
    }
  }

  function disconnectWS() {
    if (ws) {
      ws.close();
      ws = null;
    }
    wsConnected = false;
  }

  async function showAPIInfo(sessionId: string) {
    apiInfoSessionId = sessionId;
    apiInfoLoading = true;
    showAPIInfoModal = true;
    apiInfo = null;
    apiQRImage = "";
    apiQRTempUrl = "";
    qrToken = "";
    qrStatus = null;

    // Limpiar intervalo anterior si existe
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      statusCheckInterval = null;
    }

    try {
      const res = await api.get(`/api/v1/sessions/${sessionId}/api-info`);
      apiInfo = {
        appkey: res.appkey,
        authkey: res.authkey,
      };
    } catch (e: any) {
      addToast("Error al obtener información de API: " + e.message, "error");
      showAPIInfoModal = false;
    } finally {
      apiInfoLoading = false;
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    addToast(`${label} copiado al portapapeles`, "success");
  }

  // Función para obtener la URL base según el entorno
  function getBaseUrl(): string {
    if ((import.meta as any).env?.DEV) {
      return "http://localhost:8060";
    }
    return window.location.origin;
  }

  // Generar QR con la URL temporal que devuelve los datos de API
  async function generateAPIQR() {
    if (!apiInfo || !apiInfoSessionId) return;

    generatingTempUrl = true;
    try {
      const res = await api.post(
        `/api/v1/sessions/${apiInfoSessionId}/api-qr-temp`
      );
      apiQRTempUrl = res.url;
      qrToken = res.token;

      apiQRImage = await QRCode.toDataURL(apiQRTempUrl, {
        errorCorrectionLevel: "M",
        type: "image/png",
        width: 400,
        margin: 2,
      });

      await checkQRStatus();
      startStatusPolling();

      addToast(
        "QR generado exitosamente. Expira en 5 requests o 5 minutos.",
        "success"
      );
    } catch (e: any) {
      console.error("Error generando QR:", e);
      addToast("Error al generar QR: " + e.message, "error");
    } finally {
      generatingTempUrl = false;
    }
  }

  async function checkQRStatus() {
    if (!apiInfoSessionId || !qrToken) return;

    checkingStatus = true;
    try {
      const res = await api.get(
        `/api/v1/sessions/${apiInfoSessionId}/api-qr-status/${qrToken}`
      );
      qrStatus = res;
    } catch (e: any) {
      qrStatus = {
        active: false,
        expired: true,
        requests_used: 5,
        requests_remaining: 0,
        max_requests: 5,
        seconds_remaining: 0,
        expires_in_minutes: 0,
      };
    } finally {
      checkingStatus = false;
    }
  }

  function startStatusPolling() {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }

    statusCheckInterval = setInterval(async () => {
      if (qrToken && apiInfoSessionId) {
        await checkQRStatus();
        if (qrStatus?.expired) {
          if (statusCheckInterval) {
            clearInterval(statusCheckInterval);
            statusCheckInterval = null;
          }
        }
      }
    }, 10000);
  }

  function stopStatusPolling() {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      statusCheckInterval = null;
    }
  }

  function downloadQR() {
    if (!apiQRImage) return;

    try {
      const link = document.createElement("a");
      link.href = apiQRImage;
      link.download = `api-qr-${apiInfoSessionId.slice(0, 8)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      addToast("QR descargado correctamente", "success");
    } catch (e: any) {
      addToast("Error al descargar QR: " + e.message, "error");
    }
  }

  async function regenerateQR() {
    stopStatusPolling();
    apiQRImage = "";
    apiQRTempUrl = "";
    qrToken = "";
    qrStatus = null;

    await generateAPIQR();
  }

  function copyTempQRUrl() {
    if (apiQRTempUrl) {
      navigator.clipboard.writeText(apiQRTempUrl);
      addToast("URL temporal copiada al portapapeles", "success");
    }
  }

  onMount(loadSessions);
  async function submitTelegramCode() {
    if (!telegramCode || !telegramCodeSessionId) return;

    telegramCodeLoading = true;
    try {
      await api.post(
        `/api/v1/sessions/${encodeURIComponent(telegramCodeSessionId)}/telegram-code`,
        { code: telegramCode }
      );
      addToast("Código enviado correctamente", "success");
      telegramCode = "";
      // No cerrar el modal todavía, esperar confirmación de conexión
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(errorMsg, "error");
    } finally {
      telegramCodeLoading = false;
    }
  }

  // Función auxiliar para conectar una sesión (extraída de selectSession)
  async function connectSessionInternal(s: Session) {
    // Para Telegram MTProto, conectar WebSocket ANTES de llamar a /connect
    // para asegurar que esté suscrito cuando llegue el evento code_required
    if (s.type === "telegram_mtproto") {
      console.log("📡 [SESSIONS] Conectando WebSocket ANTES de /connect para Telegram MTProto");
      connectWS(s.id);
      // Esperar un momento para que el WebSocket se suscriba completamente
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log("✅ [SESSIONS] WebSocket conectado, ahora llamando a /connect");
    }
    
    await api.post(`/api/v1/sessions/${encodeURIComponent(s.id)}/connect`);
    
    // Solo conectar WebSocket para WhatsApp (que usa QR) si no es Telegram MTProto
    if (s.type === "whatsapp" || !s.type) {
      connectWS(s.id);
    } else if (s.type !== "telegram_mtproto") {
      // Para otros tipos (telegram_bot, email), no necesitan WebSocket
      statusText = "Conectado";
    }
  }

  async function resendTelegramCode() {
    if (!telegramCodeSessionId || resendCodeCountdown > 0) return;

    // Iniciar contador de 15 segundos
    resendCodeCountdown = 15;
    
    // Limpiar intervalo anterior si existe
    if (resendCodeInterval) {
      clearInterval(resendCodeInterval);
    }

    // Iniciar contador
    resendCodeInterval = setInterval(() => {
      resendCodeCountdown--;
      if (resendCodeCountdown <= 0) {
        if (resendCodeInterval) {
          clearInterval(resendCodeInterval);
          resendCodeInterval = null;
        }
      }
    }, 1000);

    try {
      // Buscar la sesión para reconectar
      const session = sessions.find(s => s.id === telegramCodeSessionId);
      if (session) {
        console.log("🔄 [RESEND] Reenviando código de verificación...");
        await connectSessionInternal(session);
        addToast("Código de verificación reenviado", "success");
      }
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(`Error al reenviar código: ${errorMsg}`, "error");
    }
  }

  // Limpiar intervalo cuando se cierra el modal
  $: if (!showTelegramCodeModal && resendCodeInterval) {
    clearInterval(resendCodeInterval);
    resendCodeInterval = null;
    resendCodeCountdown = 0;
  }

  async function submitTelegramPhone() {
    if (!telegramPhone || !telegramPhoneSessionId) return;

    telegramPhoneLoading = true;
    try {
      await api.put(
        `/api/v1/sessions/${encodeURIComponent(telegramPhoneSessionId)}/telegram-phone`,
        { phone_number: telegramPhone }
      );
      addToast("Número telefónico guardado correctamente", "success");
      
      // Cerrar modal y continuar con la conexión
      showTelegramPhoneModal = false;
      const sessionId = telegramPhoneSessionId;
      telegramPhone = "";
      telegramPhoneSessionId = "";
      
      // Recargar sesión para obtener la configuración actualizada
      await loadSessions();
      
      // Buscar la sesión actualizada
      const updatedSession = sessions.find(s => s.id === sessionId);
      if (updatedSession) {
        selectedSession = updatedSession;
        
        // Continuar con la conexión
        statusText = "Conectando a Telegram...";
        try {
          await api.post(`/api/v1/sessions/${encodeURIComponent(sessionId)}/connect`);
          connectWS(sessionId);
        } catch (e: any) {
          statusText = "Error: " + handleApiError(e);
        }
      }
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(errorMsg, "error");
    } finally {
      telegramPhoneLoading = false;
    }
  }

  async function submitTelegramPassword() {
    if (!telegramPassword || !telegramPasswordSessionId) return;

    telegramPasswordLoading = true;
    try {
      await api.post(
        `/api/v1/sessions/${encodeURIComponent(telegramPasswordSessionId)}/telegram-password`,
        { password: telegramPassword }
      );
      addToast("Contraseña 2FA enviada correctamente", "success");
      telegramPassword = "";
      // No cerrar el modal todavía, esperar confirmación de conexión
    } catch (e) {
      const errorMsg = handleApiError(e);
      addToast(errorMsg, "error");
    } finally {
      telegramPasswordLoading = false;
    }
  }

  onDestroy(() => {
    disconnectWS();
    stopWspPolling();
    stopStatusPolling();
    // Limpiar timeout si existe
    if (qrTimeout) {
      clearTimeout(qrTimeout);
      qrTimeout = null;
    }
  });
</script>

<div class="space-y-8">
  <div class="flex justify-between items-end">
    <div>
      <h2 class="text-4xl font-black text-white tracking-tight mb-2">
        Sesiones
      </h2>
      <p class="text-indigo-200/60 font-medium">
        Administra tus conexiones de mensajería
      </p>
    </div>
    <button
      on:click={() => (showCreateModal = true)}
      class="group px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
    >
      <div
        class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"
      ></div>
      <span class="relative z-10 flex items-center"
        ><span class="mr-2 text-xl leading-none">+</span> Nueva Sesión</span
      >
    </button>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
    <!-- Lista de Sesiones -->
    <div class="lg:col-span-1 space-y-4">
      {#if loading}
        <div class="text-center text-indigo-200/50 py-12 animate-pulse">
          Cargando sesiones...
        </div>
      {:else if sessions.length === 0}
        <div
          class="text-center text-indigo-200/50 py-12 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-sm"
        >
          No hay sesiones creadas
        </div>
      {:else}
        {#each sessions as session}
          <div
            class="w-full p-5 rounded-2xl border transition-all duration-300 group relative overflow-hidden {selectedSession?.id ===
            session.id
              ? 'bg-white/10 border-pink-500/50 shadow-lg shadow-pink-500/10'
              : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'}"
          >
            {#if selectedSession?.id === session.id}
              <div
                class="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-pink-500 to-purple-500"
              ></div>
            {/if}
            <button
              on:click={() => selectSession(session)}
              class="w-full text-left"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div
                    class="font-bold text-white text-lg mb-1 group-hover:text-pink-200 transition-colors"
                  >
                    {session.label}
                  </div>
                  <div
                    class="text-sm text-indigo-200/60 flex items-center gap-2 flex-wrap"
                  >
                    <span
                      class="text-xs bg-black/30 px-2 py-0.5 rounded-full font-mono"
                      >{session.number || "Sin número"}</span
                    >
                    {#if session.restricted}
                      <span
                        class="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full flex items-center gap-1"
                        title="WhatsApp limitó envíos a contactos nuevos (error 463)"
                      >
                        <span class="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                        Restringido
                        {#if session.restricted_until}
                          · hasta {formatRestrictedUntil(session.restricted_until)}
                        {/if}
                      </span>
                    {:else if session.connected}
                      <span
                        class="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1"
                      >
                        <span
                          class="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"
                        ></span>
                        Conectado
                      </span>
                    {:else}
                      <span
                        class="text-xs bg-gray-500/20 text-gray-300 px-2 py-0.5 rounded-full"
                        >Desconectado</span
                      >
                    {/if}
                  </div>
                </div>
                <div class="text-[10px] text-indigo-200/30 font-mono mt-1">
                  {session.id.slice(0, 8)}
                </div>
              </div>
            </button>
            <div
              class="mt-3 pt-3 border-t border-white/5 flex gap-2"
              on:click|stopPropagation
              on:keydown|stopPropagation
              role="presentation"
            >
              {#if session.connected}
                <button
                  on:click={() => openConfirm("disconnect", session.id)}
                  class="flex-1 px-3 py-1.5 text-xs font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 rounded-lg transition-colors"
                  title="Desconectar"
                >
                  ⏸️ Parar
                </button>
                <button
                  on:click={() => openConfirm("logout", session.id)}
                  class="flex-1 px-3 py-1.5 text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 rounded-lg transition-colors"
                  title="Desvincular"
                >
                  🔓 Desvincular
                </button>
                <button
                  on:click={() => showAPIInfo(session.id)}
                  class="px-3 py-1.5 text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors"
                  title="Ver API Info"
                >
                  🔑 API
                </button>
              {/if}
            </div>
          </div>
        {/each}
      {/if}
    </div>

    <!-- Panel de Detalles / QR -->
    <div class="lg:col-span-2">
      {#if selectedSession}
        <div
          class="bg-white/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden"
        >
          <!-- Background decoration -->
          <div
            class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"
          ></div>

          <div class="flex justify-between items-start mb-8 relative z-10">
            <div>
              <h3 class="text-2xl font-bold text-white mb-1">
                {selectedSession.label}
              </h3>
              <p class="text-pink-300 font-medium flex items-center gap-2">
                {statusText}
              </p>
            </div>
            <div
              class="flex items-center gap-3 bg-black/20 px-4 py-2 rounded-full border border-white/5"
              title={wspHint || "Estado de la conexión WhatsApp"}
            >
              <span class="relative flex h-3 w-3">
                {#if wspState === "ok"}
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                  ></span>
                {/if}
                <span
                  class="relative inline-flex rounded-full h-3 w-3 {wspState ===
                  'ok'
                    ? 'bg-emerald-500'
                    : wspState === 'problem'
                      ? 'bg-amber-400'
                      : 'bg-red-500'}"
                ></span>
              </span>
              <span class="text-xs font-bold tracking-wide text-indigo-100/80"
                >{wspLabel(wspState)}</span
              >
            </div>
          </div>

          <div
            class="flex flex-col items-center justify-center min-h-[350px] bg-black/20 rounded-2xl border border-white/5 p-8 relative"
          >
            {#if qrImage}
              <div
                class="bg-white p-4 rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-500"
              >
                <img
                  src={qrImage}
                  alt="QR Code"
                  class="w-64 h-64 mix-blend-multiply"
                />
              </div>
              <p
                class="mt-6 text-indigo-300/90 text-base font-medium animate-pulse"
              >
                {#if selectedSession && supportsQR(selectedSession.type)}
                  Escanea con WhatsApp > Dispositivos vinculados
                {:else}
                  Conectando sesión...
                {/if}
              </p>
            {:else if statusText.includes("Código de verificación requerido") || showTelegramCodeModal}
              <div
                class="text-center animate-in fade-in slide-in-from-bottom-4 duration-700 w-full"
              >
                <div class="text-7xl mb-6 filter drop-shadow-lg">📱</div>
                <h4 class="text-2xl font-bold text-white mb-2">
                  Código de Verificación Requerido
                </h4>
                <p class="text-indigo-300/90 text-base mb-6 font-medium">
                  Telegram ha enviado un código a tu teléfono.
                  {#if !showTelegramCodeModal}
                    <br />
                    <span class="text-pink-300 font-bold"
                      >El modal se abrirá automáticamente...</span
                    >
                  {/if}
                </p>
              </div>
            {:else if selectedSession?.restricted}
              <div
                class="text-center animate-in fade-in slide-in-from-bottom-4 duration-700 w-full"
              >
                <div class="text-7xl mb-6 filter drop-shadow-lg">⚠️</div>
                <h4 class="text-2xl font-bold text-red-300 mb-2">
                  {selectedSession
                    ? getSessionTypeName(selectedSession.type)
                    : "Sesión"} Restringido
                </h4>
                <p class="text-red-200/80 text-base mb-6 font-medium">
                  WhatsApp limitó envíos a contactos nuevos
                  {#if selectedSession.restricted_until}
                    <br />Hasta {formatRestrictedUntil(selectedSession.restricted_until)}
                  {/if}
                </p>
                {#if selectedSession && selectedSession.id}
                  <div class="flex gap-3 justify-center">
                    <button
                      on:click={() =>
                        selectedSession &&
                        openConfirm("disconnect", selectedSession.id)}
                      class="px-4 py-2 text-sm font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 rounded-lg transition-colors"
                    >
                      ⏸️ Parar
                    </button>
                    <button
                      on:click={() =>
                        selectedSession &&
                        openConfirm("logout", selectedSession.id)}
                      class="px-4 py-2 text-sm font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 rounded-lg transition-colors"
                    >
                      🔓 Desvincular
                    </button>
                  </div>
                {/if}
              </div>
            {:else if statusText.includes("Vinculado") || statusText.includes("Conectada") || (selectedSession && selectedSession.connected)}
              <div
                class="text-center animate-in fade-in slide-in-from-bottom-4 duration-700 w-full"
              >
                <div class="text-7xl mb-6 filter drop-shadow-lg">📱</div>
                <h4 class="text-2xl font-bold text-white mb-2">
                  {selectedSession
                    ? getSessionTypeName(selectedSession.type)
                    : "Sesión"} Conectado
                </h4>
                <p class="text-indigo-300/90 text-base mb-6 font-medium">
                  Esta sesión está lista para enviar mensajes
                </p>
                {#if selectedSession && selectedSession.id}
                  <div class="flex gap-3 justify-center">
                    <button
                      on:click={() =>
                        selectedSession &&
                        openConfirm("disconnect", selectedSession.id)}
                      class="px-4 py-2 text-sm font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 rounded-lg transition-colors"
                    >
                      ⏸️ Parar
                    </button>
                    <button
                      on:click={() =>
                        selectedSession &&
                        openConfirm("logout", selectedSession.id)}
                      class="px-4 py-2 text-sm font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/30 rounded-lg transition-colors"
                    >
                      🔓 Desvincular
                    </button>
                  </div>
                {/if}
              </div>
            {:else}
              <div class="flex flex-col items-center gap-4">
                <div
                  class="w-12 h-12 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin"
                ></div>
                <div class="text-indigo-300/90 text-base font-medium">
                  Iniciando conexión...
                </div>
              </div>
            {/if}
          </div>

          <!-- Logs -->
          <div class="mt-8">
            <h4
              class="text-xs font-bold text-indigo-200/40 uppercase tracking-wider mb-3"
            >
              Logs de conexión
            </h4>
            <div
              class="bg-black/40 rounded-xl p-4 h-40 overflow-y-auto font-mono text-xs text-indigo-100/70 border border-white/5 shadow-inner custom-scrollbar"
            >
              {#each logs as log}
                <div
                  class="mb-1.5 border-b border-white/5 pb-1.5 last:border-0 last:pb-0 flex gap-2"
                >
                  <span class="text-white/20">➜</span>
                  <span>{log}</span>
                </div>
              {/each}
            </div>
          </div>
        </div>
      {:else}
        <div
          class="h-full flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-3xl border-dashed p-12 text-center"
        >
          <div class="text-6xl mb-4 opacity-20">👋</div>
          <h3 class="text-xl font-bold text-white mb-2">Bienvenido al Panel</h3>
          <p class="text-indigo-200/40 max-w-sm">
            Selecciona una sesión del menú de la izquierda para ver sus detalles
            o escanear el código QR.
          </p>
        </div>
      {/if}
    </div>
  </div>
</div>

<!-- Modal Crear Sesión -->
{#if showCreateModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
    >
      <!-- Modal glow -->
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500"
      ></div>

      <h3 class="text-2xl font-black text-white mb-6">Nueva Sesión</h3>

      <div class="space-y-5">
        <div>
          <label
            for="newLabel"
            class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            >Nombre (Label)</label
          >
          <input
            bind:value={newLabel}
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
            placeholder="Ej: Ventas #1"
          />
        </div>

          <div>
            <label
            for="newType"
            class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            >Tipo de Sesión</label
          >
          <select
            bind:value={newType}
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none"
          >
            <option value="whatsapp">WhatsApp</option>
            <!-- <option value="telegram_mtproto">Telegram MTProto</option> -->
            <option value="telegram_bot">Telegram Bot</option>
            <option value="email">Email (SMTP)</option>
          </select>
        </div>

        <!-- Campo Number solo para WhatsApp y Email, no para Telegram MTProto -->
        {#if newType !== "telegram_mtproto"}
          <div>
            <label
              for="newNumber"
              class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            >
              {newType === "email"
                ? "Email / Identificador"
                : "Número (Opcional)"}
            </label>
            <input
              bind:value={newNumber}
              class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
              placeholder={newType === "email" ? "ejemplo@email.com" : "57300..."}
            />
          </div>
        {/if}

        <!-- Telegram MTProto Configuration -->
        {#if newType === "telegram_mtproto"}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p
              class="text-xs font-bold text-indigo-200 uppercase tracking-wider"
            >
              Configuración Telegram MTProto
            </p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="apiId"
                >API ID</label
              >
              <input
                bind:value={telegramConfig.api_id}
                type="number"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="12345678"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="apiHash"
                >API Hash</label
              >
              <input
                bind:value={telegramConfig.api_hash}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="abcdef1234567890abcdef1234567890"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="phoneNumber"
                >Número de Teléfono <span class="text-red-400">*</span></label
              >
              <input
                bind:value={telegramConfig.phone_number}
                type="tel"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="+51972053723"
              />
              <p class="text-xs text-indigo-300/50 mt-1">
                <span class="text-red-400 font-bold">Requerido</span> para autenticación. Formato internacional: +51972053723
              </p>
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="password"
                >Contraseña 2FA (Opcional)</label
              >
              <input
                bind:value={telegramConfig.password}
                type="password"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="Solo si tienes 2FA activado"
              />
              <p class="text-xs text-indigo-300/50 mt-1">
                Solo si tu cuenta tiene autenticación de dos factores activada
              </p>
            </div>
          </div>
        {/if}

        <!-- Telegram Bot Configuration -->
        {#if newType === "telegram_bot"}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p
              class="text-xs font-bold text-indigo-200 uppercase tracking-wider"
            >
              Configuración Telegram Bot
            </p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="botToken"
                >Bot Token</label
              >
              <input
                bind:value={telegramConfig.bot_token}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
              />
            </div>
          </div>
        {/if}

        <!-- Email Configuration -->
        {#if newType === "email"}
          <div class="space-y-3 pt-3 border-t border-white/10">
            <p
              class="text-xs font-bold text-indigo-200 uppercase tracking-wider"
            >
              Configuración SMTP
            </p>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="smtpHost"
                >SMTP Host</label
              >
              <input
                bind:value={emailConfig.smtp_host}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="smtpPort"
                >SMTP Port</label
              >
              <input
                bind:value={emailConfig.smtp_port}
                type="number"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="587"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="smtpUser"
                >SMTP User</label
              >
              <input
                bind:value={emailConfig.smtp_user}
                type="email"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="mi@email.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="smtpPassword"
                >SMTP Password</label
              >
              <input
                bind:value={emailConfig.smtp_password}
                type="password"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="fromEmail"
                >From Email (Opcional)</label
              >
              <input
                bind:value={emailConfig.from_email}
                type="email"
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="remitente@email.com"
              />
            </div>
            <div>
              <label class="block text-xs text-indigo-300/70 mb-1"
                for="fromName"
                >From Name (Opcional)</label
              >
              <input
                bind:value={emailConfig.from_name}
                class="w-full px-4 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20"
                placeholder="Mi Nombre"
              />
            </div>
            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                bind:checked={emailConfig.tls}
                id="tls-check"
                class="w-4 h-4 rounded bg-black/30 border border-white/10 text-pink-500 focus:ring-pink-500/50"
              />
              <label for="tls-check" class="text-xs text-indigo-300/70"
                >Usar TLS</label
              >
            </div>
          </div>
        {/if}
      </div>

      <div class="flex justify-end gap-3 mt-10">
        <button
          on:click={() => (showCreateModal = false)}
          class="px-6 py-3 text-indigo-200/60 hover:text-white transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          on:click={createSession}
          disabled={creating || !newLabel}
          class="group px-6 py-3 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden"
        >
          <div
            class="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-700 -skew-x-12 -translate-x-full"
          ></div>
          <span class="relative z-10"
            >{creating ? "Creando..." : "Crear Sesión"}</span
          >
        </button>
      </div>
    </div>
  </div>
{/if}

<ConfirmModal
  isOpen={showConfirm}
  title={getConfirmTitle()}
  message={getConfirmMessage()}
  confirmText="Confirmar"
  on:confirm={handleConfirm}
  on:cancel={() => {
    showConfirm = false;
    confirmAction = null;
    confirmSessionId = "";
  }}
/>

<!-- Telegram Phone Modal -->
{#if showTelegramPhoneModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
    >
      <!-- Modal glow -->
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"
      ></div>

      <h3 class="text-2xl font-black text-white mb-2">
        Número Telefónico Requerido
      </h3>
      <p class="text-indigo-200/60 mb-6">
        Para conectar Telegram MTProto, necesitamos el número telefónico al cual
        Telegram enviará el código de verificación. Ingresa tu número en formato
        internacional (ej: +51972053723):
      </p>

      <div class="space-y-4">
        <div>
          <label
            class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            for="telegramPhone"
            >Número Telefónico</label
          >
          <input
            bind:value={telegramPhone}
            type="tel"
            placeholder="+51972053723"
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-lg font-mono focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none placeholder-white/20"
            on:keydown={(e) => {
              if (e.key === "Enter" && telegramPhone && !telegramPhoneLoading) {
                submitTelegramPhone();
              }
            }}
          />
        </div>
      </div>

      <div class="flex justify-end gap-3 mt-8">
        <button
          on:click={() => {
            showTelegramPhoneModal = false;
            telegramPhone = "";
            telegramPhoneSessionId = "";
          }}
          class="px-6 py-3 text-indigo-200/60 hover:text-white transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          on:click={submitTelegramPhone}
          disabled={telegramPhoneLoading || !telegramPhone}
          class="px-6 py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 hover:from-blue-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {telegramPhoneLoading ? "Guardando..." : "Guardar y Conectar"}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Telegram Password Modal -->
{#if showTelegramPasswordModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
    >
      <!-- Modal glow -->
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500"
      ></div>

      <h3 class="text-2xl font-black text-white mb-2">
        Contraseña 2FA Requerida
      </h3>
      <p class="text-indigo-200/60 mb-6">
        Telegram requiere la contraseña de verificación en dos pasos (2FA) para
        completar la autenticación. Ingresa tu contraseña 2FA:
      </p>

      <div class="space-y-4">
        <div>
          <label
            class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            for="telegramPassword"
            >Contraseña 2FA</label
          >
          <input
            bind:value={telegramPassword}
            type="password"
            placeholder="Tu contraseña 2FA"
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all outline-none placeholder-white/20"
            on:keydown={(e) => {
              if (e.key === "Enter" && telegramPassword && !telegramPasswordLoading) {
                submitTelegramPassword();
              }
            }}
          />
        </div>
      </div>

      <div class="flex justify-end gap-3 mt-8">
        <button
          on:click={() => {
            showTelegramPasswordModal = false;
            telegramPassword = "";
            telegramPasswordSessionId = "";
          }}
          class="px-6 py-3 text-indigo-200/60 hover:text-white transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          on:click={submitTelegramPassword}
          disabled={telegramPasswordLoading || !telegramPassword}
          class="px-6 py-3 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 hover:from-red-400 hover:via-orange-400 hover:to-yellow-400 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {telegramPasswordLoading ? "Enviando..." : "Enviar Contraseña"}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Telegram Code Modal -->
{#if showTelegramCodeModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200"
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 relative overflow-hidden"
    >
      <!-- Modal glow -->
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"
      ></div>

      <h3 class="text-2xl font-black text-white mb-2">
        Código de Verificación Telegram
      </h3>
      <p class="text-indigo-200/60 mb-6">
        <span class="block mb-2">
          📱 <strong>Revisa tu aplicación móvil de Telegram</strong> (en tu celular).
        </span>
        <span class="block mb-2">
          Telegram ha enviado un código de verificación a tu aplicación móvil.
        </span>
        <span class="block">
          Ingresa el código que aparece en tu celular:
        </span>
      </p>

      <div class="space-y-4">
        <div>
          <label
            class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
            for="telegramCode"
            >Código de Verificación</label
          >
          <input
            bind:value={telegramCode}
            type="text"
            maxlength="10"
            placeholder="12345"
            class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none placeholder-white/20"
            on:keydown={(e) => {
              if (e.key === "Enter" && telegramCode && !telegramCodeLoading) {
                submitTelegramCode();
              }
            }}
          />
        </div>
        
        <!-- Botón para reenviar código -->
        <div class="flex justify-center">
          <button
            on:click={resendTelegramCode}
            disabled={resendCodeCountdown > 0}
            class="px-4 py-2 text-sm bg-gray-800/50 hover:bg-gray-800 border border-white/10 hover:border-white/20 text-indigo-200 hover:text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-800/50 disabled:hover:border-white/10"
          >
            {resendCodeCountdown > 0 
              ? `Reenviar código (${resendCodeCountdown}s)` 
              : "🔄 Reenviar código"}
          </button>
        </div>
      </div>

      <div class="flex justify-end gap-3 mt-8">
        <button
          on:click={() => {
            showTelegramCodeModal = false;
            telegramCode = "";
            telegramCodeSessionId = "";
            // Limpiar intervalo al cerrar
            if (resendCodeInterval) {
              clearInterval(resendCodeInterval);
              resendCodeInterval = null;
            }
            resendCodeCountdown = 0;
          }}
          class="px-6 py-3 text-indigo-200/60 hover:text-white transition-colors font-medium"
        >
          Cancelar
        </button>
        <button
          on:click={submitTelegramCode}
          disabled={telegramCodeLoading || !telegramCode}
          class="px-6 py-3 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 hover:from-blue-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {telegramCodeLoading ? "Enviando..." : "Enviar Código"}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- API Info Modal -->
{#if showAPIInfoModal}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
    transition:fade
  >
    <div
      class="bg-gray-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] p-6 relative overflow-hidden flex flex-col"
      transition:slide
    >
      <div
        class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"
      ></div>

      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-black text-white">Información de API</h3>
        <button
          on:click={() => {
            showAPIInfoModal = false;
            apiInfo = null;
            apiQRImage = "";
            apiQRTempUrl = "";
            qrToken = "";
            qrStatus = null;
            stopStatusPolling();
          }}
          class="px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
        >
          ✕ Cerrar
        </button>
      </div>

      {#if apiInfoLoading}
        <div class="text-center py-12">
          <p class="text-indigo-200/60 animate-pulse">
            Cargando información...
          </p>
        </div>
      {:else if apiInfo}
        <div class="flex gap-6 flex-1 overflow-hidden">
          <!-- Columna izquierda: Campos y ejemplo -->
          <div class="flex-1 space-y-4 overflow-y-auto pr-2">
            <p class="text-xs text-indigo-300/70 font-medium">
              Usa estos valores para enviar mensajes desde aplicaciones
              externas:
            </p>

            <!-- AppKey (Session ID) -->
            <div class="space-y-1.5">
              <label
                class="block text-xs font-bold text-indigo-200 uppercase tracking-wider"
                for="appKey"
                >AppKey (Session ID)</label
              >
              <div class="flex gap-2">
                <input
                  type="text"
                  value={apiInfo.appkey}
                  readonly
                  class="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                />
                <button
                  on:click={() =>
                    apiInfo && copyToClipboard(apiInfo.appkey, "AppKey")}
                  class="px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium"
                  title="Copiar AppKey"
                >
                  📋
                </button>
              </div>
              <p class="text-xs text-indigo-300/60">ID de la sesión</p>
            </div>

            <!-- AuthKey (API Token) -->
            <div class="space-y-1.5">
              <label
                class="block text-xs font-bold text-indigo-200 uppercase tracking-wider"
                for="authKey"
                >AuthKey (API Token)</label
              >
              <div class="flex gap-2">
                <input
                  type="text"
                  value={apiInfo.authkey}
                  readonly
                  class="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                />
                <button
                  on:click={() =>
                    apiInfo && copyToClipboard(apiInfo.authkey, "AuthKey")}
                  class="px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium"
                  title="Copiar AuthKey"
                >
                  📋
                </button>
              </div>
              <p class="text-xs text-indigo-300/60">
                Token de autenticación para la API externa
              </p>
            </div>

            <!-- Ejemplo de uso -->
            <div class="bg-black/40 border border-white/10 rounded-xl p-3">
              <label
                class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2"
                for="exampleRequest"
                >Ejemplo de Request</label
              >
              {#each [sessions.find(s => s.id === apiInfoSessionId)] as apiSession}
                {#if apiSession && apiSession.type === "email"}
                  <!-- Ejemplo para sesiones de Email -->
                  <pre
                    class="text-xs text-indigo-200/80 font-mono overflow-x-auto bg-black/50 p-2 rounded-lg border border-white/5">{`POST /api/v1/api/sendEmail
Authorization: Bearer ${apiInfo.authkey}

{
  "to": "destino@correo.com",
  "subject": "Hola desde la API",
  "body": "Contenido del correo en texto plano",
  "appkey": "${apiInfo.appkey}",
  "authkey": "${apiInfo.authkey}"
}`}</pre>
                {:else if apiSession && apiSession.type === "telegram_bot"}
                  <!-- Ejemplo para sesiones de Telegram Bot (to = chat_id) -->
                  <pre
                    class="text-xs text-indigo-200/80 font-mono overflow-x-auto bg-black/50 p-2 rounded-lg border border-white/5">{`POST /api/v1/api/sendTelegramBot
Authorization: Bearer ${apiInfo.authkey}

{
  "to": "123456789", // chat_id del usuario o grupo
  "message": "Hola desde el bot de Telegram",
  "appkey": "${apiInfo.appkey}",
  "authkey": "${apiInfo.authkey}"
}`}</pre>
                {:else}
                  <!-- Ejemplo por defecto para WhatsApp -->
                  <pre
                    class="text-xs text-indigo-200/80 font-mono overflow-x-auto bg-black/50 p-2 rounded-lg border border-white/5">{`POST /api/v1/api/sendText
Authorization: Bearer ${apiInfo.authkey}

{
  "to": "51972053723",
  "message": "Hola mundo",
  "appkey": "${apiInfo.appkey}",
  "authkey": "${apiInfo.authkey}"
}`}</pre>
                {/if}
              {:else}
                <!-- Fallback si no se encuentra la sesión -->
                <pre
                  class="text-xs text-indigo-200/80 font-mono overflow-x-auto bg-black/50 p-2 rounded-lg border border-white/5">{`POST /api/v1/api/sendText
Authorization: Bearer ${apiInfo.authkey}

{
  "to": "51972053723",
  "message": "Hola mundo",
  "appkey": "${apiInfo.appkey}",
  "authkey": "${apiInfo.authkey}"
}`}</pre>
              {/each}
            </div>
          </div>

          <!-- Columna derecha: QR Code (solo para WhatsApp) -->
          {#each [sessions.find(s => s.id === apiInfoSessionId)] as apiSessionForQR}
            {#if apiSessionForQR && (apiSessionForQR.type === 'whatsapp' || !apiSessionForQR.type)}
            <div
              class="w-80 flex-shrink-0 border-l border-white/10 pl-6 flex flex-col"
            >
              <div class="flex items-center justify-between mb-3">
                <label
                  class="block text-xs font-bold text-indigo-200 uppercase tracking-wider"
                  for="qrStatus"
                  >Código QR</label
                >
                {#if qrStatus}
                  {@const statusColor = qrStatus.active
                    ? qrStatus.requests_remaining > 2 &&
                      qrStatus.seconds_remaining > 180
                      ? "emerald"
                      : "yellow"
                    : "red"}
                  {@const statusText = qrStatus.active
                    ? qrStatus.requests_remaining > 2 &&
                      qrStatus.seconds_remaining > 180
                      ? "Activo"
                      : "Casi expirado"
                    : "Expirado"}
                  <span
                    class="px-2 py-1 rounded-full text-xs font-bold
                    {statusColor === 'emerald'
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      : ''}
                    {statusColor === 'yellow'
                      ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                      : ''}
                    {statusColor === 'red'
                      ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                      : ''}
                  "
                  >
                    {qrStatus.active ? "●" : "○"}
                    {statusText}
                  </span>
                {/if}
              </div>

              {#if !apiQRImage}
                <!-- Botón para generar QR -->
                <div
                  class="flex flex-col items-center justify-center gap-4 flex-1 py-8"
                >
                  <p class="text-sm text-indigo-300/70 text-center">
                    Genera un código QR seguro para compartir los datos de
                    configuración
                  </p>
                  <button
                    on:click={generateAPIQR}
                    disabled={generatingTempUrl}
                    class="px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 hover:from-purple-400 hover:via-pink-400 hover:to-indigo-400 text-white rounded-xl font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Generar QR seguro"
                  >
                    {generatingTempUrl ? "Generando..." : "📱 Generar QR"}
                  </button>
                  <p class="text-xs text-indigo-300/50 text-center">
                    El QR contiene una URL temporal que expira en 5 requests o 5
                    minutos
                  </p>
                </div>
              {:else}
                <!-- QR generado -->
                <div class="flex flex-col items-center gap-3 flex-1">
                  <div class="bg-white p-3 rounded-xl">
                    <img
                      src={apiQRImage}
                      alt="API QR Code"
                      class="w-full max-w-[240px] h-auto"
                    />
                  </div>

                  <!-- Estado del QR -->
                  {#if qrStatus}
                    <div
                      class="w-full bg-black/40 border border-white/10 rounded-lg p-3 space-y-2"
                    >
                      <div class="flex items-center justify-between text-xs">
                        <span class="text-indigo-300/70">Requests:</span>
                        <span class="font-bold text-white">
                          {qrStatus.requests_used}/{qrStatus.max_requests}
                          {#if qrStatus.requests_remaining > 0}
                            <span class="text-emerald-300"
                              >({qrStatus.requests_remaining} restantes)</span
                            >
                          {:else}
                            <span class="text-red-300">(agotados)</span>
                          {/if}
                        </span>
                      </div>
                      <div class="flex items-center justify-between text-xs">
                        <span class="text-indigo-300/70">Tiempo restante:</span>
                        <span
                          class="font-bold {qrStatus.seconds_remaining > 180
                            ? 'text-emerald-300'
                            : qrStatus.seconds_remaining > 60
                              ? 'text-yellow-300'
                              : 'text-red-300'}"
                        >
                          {qrStatus.seconds_remaining > 0
                            ? `${Math.floor(qrStatus.seconds_remaining / 60)}:${String(qrStatus.seconds_remaining % 60).padStart(2, "0")}`
                            : "Expirado"}
                        </span>
                      </div>
                    </div>
                  {:else if checkingStatus}
                    <p class="text-xs text-indigo-300/60 animate-pulse">
                      Verificando estado...
                    </p>
                  {/if}

                  <p class="text-xs text-indigo-300/70 text-center">
                    Escanea con tu app para obtener los datos
                  </p>

                  <!-- Botones de acción -->
                  <div class="flex flex-col gap-2 w-full">
                    <button
                      on:click={downloadQR}
                      class="w-full px-3 py-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2"
                      title="Descargar QR"
                    >
                      ⬇️ Descargar
                    </button>
                    <button
                      on:click={() => {
                        navigator.clipboard.writeText(apiQRImage);
                        addToast(
                          "QR copiado al portapapeles (como imagen)",
                          "success"
                        );
                      }}
                      class="w-full px-3 py-2 bg-blue-500/20 text-blue-300 border border-blue-500/30 hover:bg-blue-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2"
                      title="Copiar QR"
                    >
                      📋 Copiar QR
                    </button>
                    <button
                      on:click={regenerateQR}
                      disabled={generatingTempUrl}
                      class="w-full px-3 py-2 bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 rounded-lg transition-colors text-xs font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerar QR (revoca el anterior)"
                    >
                      🔄 {generatingTempUrl ? "Regenerando..." : "Regenerar QR"}
                    </button>
                  </div>

                  <!-- URL temporal -->
                  {#if apiQRTempUrl}
                    <div class="w-full space-y-1.5 mt-2">
                      <label
                        class="block text-xs font-bold text-indigo-200 uppercase tracking-wider"
                        for="apiQRTempUrl"
                        >URL Temporal</label
                      >
                      <div class="flex gap-2">
                        <input
                          type="text"
                          value={apiQRTempUrl}
                          readonly
                          class="flex-1 px-2 py-1.5 rounded-lg bg-black/30 border border-white/10 text-white text-xs font-mono"
                        />
                        <button
                          on:click={copyTempQRUrl}
                          class="px-2 py-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 rounded-lg transition-colors text-xs"
                          title="Copiar URL"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
            {/if}
          {/each}
        </div>
      {:else}
        <div class="text-center py-12">
          <p class="text-indigo-200/60">
            No se pudo obtener la información de API.
          </p>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.2);
  }
</style>
